import { Text } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import Spinner from '~/components/Spinner';
import {
  MEDIA_PACK_ENTITY_QUERY_KEY,
  TIKTOK_CONTENT_QUERY_KEY,
  TIKTOK_INSIGHTS_QUERY_KEY,
} from '~/queries/keys';
import { selectCreator } from '~/selectors/creator';
import {
  CreatorBenchmarks,
  DefaultMediaPackType,
  InstagramInsightType,
  TiktokInsightType,
  TiktokType,
} from '~/types';

import { patchMediaPack } from '../../MediaPackDetailDefaultScreen.services';
import { UpdateContentSubmitValues } from '../MediaPackDetailSelectContentModal/MediaPackDetailSelectContentModal.types';
import { getTiktokContents, getTiktokInsights } from './TikTokSection.services';
import { TiktokSection } from './TiktokSection';

interface Props {
  mediaPack: DefaultMediaPackType;
  creatorBenchmarks: CreatorBenchmarks;
  creatorInsights: InstagramInsightType;
  isSharedView?: boolean;
}

export const TiktokSectionContainer: React.FC<Props> = ({
  mediaPack,
  creatorBenchmarks,
  creatorInsights,
  isSharedView,
}) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const creator = useSelector(selectCreator);
  const { sharedToken } = router.query;

  const {
    data: tiktokInsights = {} as TiktokInsightType,
    isLoading,
    error,
  } = useQuery<TiktokInsightType, any>(
    [TIKTOK_INSIGHTS_QUERY_KEY, creator?.tiktokInsight?.id],
    () =>
      getTiktokInsights(
        mediaPack?.id,
        sharedToken as string | undefined,
        creator?.id,
      ),
    {
      enabled: !!creator?.tiktokInsight?.id,
    },
  );

  const {
    data: ttContents = [],
    isInitialLoading: isTiktokContentLoading,
    error: tiktokContentsError,
  } = useQuery<TiktokType[], any>(
    [TIKTOK_CONTENT_QUERY_KEY],
    () =>
      getTiktokContents(
        mediaPack?.tiktokContents?.reduce((acc, { id }) => {
          if (id) {
            acc.push(id);
          }

          return acc;
        }, []),
        sharedToken as string | undefined,
        creator?.id,
      ),
    { enabled: !!mediaPack?.tiktokContents?.length, keepPreviousData: true },
  );

  const handleUpdateMediaPack = async (values: UpdateContentSubmitValues) => {
    await patchMediaPack({
      id: mediaPack?.id,
      data: {
        tiktokContents: values.contents,
      },
      _impersonateOnce: creator?.id,
    });

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [MEDIA_PACK_ENTITY_QUERY_KEY],
      }),
      queryClient.invalidateQueries({
        queryKey: [TIKTOK_CONTENT_QUERY_KEY],
      }),
    ]);
  };

  const topSixTtContents = ttContents.slice(0, 6);

  if (error || tiktokContentsError) {
    <Text color="error.500">Something went wrong.</Text>;
  }

  if (isLoading || isTiktokContentLoading) {
    return <Spinner centered aria-label="Loading data" />;
  }

  return (
    <TiktokSection
      creatorBenchmarks={creatorBenchmarks}
      creatorInsights={creatorInsights}
      mediaPack={mediaPack}
      ttContents={topSixTtContents}
      ttInsights={tiktokInsights}
      onUpdateMediaPack={handleUpdateMediaPack}
      isSharedView={isSharedView}
    />
  );
};
